/**
 * Years as the periods of a listing, taken from `effective`.
 *
 * The publications bar keys off `publication_year`, a keyword index that can
 * be asked for a set of years at once. Most types have no such field - what
 * they all share is `effective`, a date index, which answers ranges instead:
 * `between 2019-01-01 and 2019-12-31`.
 *
 * That difference has one consequence worth knowing. A range covers a
 * contiguous span, so a selection of the most recent years, or of one year,
 * is exact - which is every selection this bar makes. Were a selection ever
 * to skip a year, the query would return the years in between as well, and
 * the listing trims them when it groups. Nothing is ever missing; at worst
 * something extra is fetched.
 *
 * `effective` is worth keying off only because it now says something true.
 * The migration had set it to the day of the import on everything; it now
 * carries each item's own date - see doc 11.
 */

const FIELD = 'effective';
const BETWEEN = 'plone.app.querystring.operation.date.between';

/**
 * The first year the site has anything from.
 *
 * Deliberately early rather than tight. Counting is what decides which years
 * get a tab, so a candidate that holds nothing costs one small query and then
 * disappears; a candidate that is missing loses a year of content silently.
 * Set to 2008 at first, this bar quietly dropped the theses from 2003, 2005
 * and 2007.
 */
const FIRST_YEAR = 1998;

const yearsFromQuery = (querystring) => {
  const found = new Set();
  (querystring?.query || []).forEach((c) => {
    if (c.i !== FIELD) return;
    (Array.isArray(c.v) ? c.v : [c.v]).forEach((v) => {
      const y = String(v || '').slice(0, 4);
      if (/^\d{4}$/.test(y)) found.add(y);
    });
  });
  return [...found];
};

/**
 * Every year the query could cover.
 *
 * A `between` criterion on the block names its own span; otherwise the
 * candidates run from the site's first year to this one, and the counts throw
 * away the years that hold nothing.
 */
const periods = (querystring) => {
  const named = yearsFromQuery(querystring).sort();
  const from = Number(named[0] || FIRST_YEAR);
  const to = Number(named[named.length - 1] || new Date().getFullYear());
  const out = [];
  for (let y = to; y >= from; y -= 1) out.push(String(y));
  return out;
};

/** The query, restricted to the span the selection covers. */
const narrow = (querystring, selection) => {
  const rest = { ...querystring };
  const kept = (rest.query || []).filter((c) => c.i !== FIELD);
  const years = selection.map(Number).sort((a, b) => a - b);
  const from = years[0];
  const to = years[years.length - 1];
  return {
    ...rest,
    query: [
      ...kept,
      { i: FIELD, o: BETWEEN, v: [`${from}-01-01`, `${to}-12-31`] },
    ],
  };
};

const of = (item) => String(item[FIELD] || '').slice(0, 4);

export const effectiveYearSpec = {
  name: 'effectiveYears',
  param: 'year',
  navLabel: 'Years',
  periods,
  narrow,
  of,
  label: (year) => year,
  compare: (a, b) => (a === b ? 0 : a < b ? -1 : 1),
};

export default effectiveYearSpec;
